import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import { aplica, componenteNecessario, fmt, p2Necessaria, p3Necessaria, situacao } from './calculo';
import type { DisciplinaDesempenho } from './types';

const P2 = { T2: 0.3, IND2: 0.5, GRU2: 0.2 };
const P3_COMPOSTA = { EX3: 0.3, IND3: 0.7 };

function Controle({
  rotulo,
  peso,
  valor,
  onChange,
}: {
  rotulo: string;
  peso: number;
  valor: number;
  onChange: (v: number) => void;
}) {
  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2">
          {rotulo}{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            peso {fmt(peso, 1)}
          </Typography>
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {fmt(valor, 1)}
        </Typography>
      </Stack>
      <Slider
        value={valor}
        min={0}
        max={10}
        step={0.1}
        onChange={(_, v) => onChange(v as number)}
        aria-label={rotulo}
      />
    </Box>
  );
}

/** "E se...?": o aluno mexe nas notas que faltam e vê na hora a média e a situação. */
export function Simulador({ d }: { d: DisciplinaDesempenho }) {
  const p1 = d.notas.P1;
  const repetir = { T2: d.notas.T1 ?? 0, IND2: d.notas.IND1 ?? 0, GRU2: d.notas.GRU1 ?? 0 };
  const [p2, setP2] = useState(repetir);
  const [p3, setP3] = useState({ P3: 6, EX3: 6, IND3: 6 });

  if (typeof p1 !== 'number') {
    return (
      <Alert severity="info">
        A sua P1 ainda está pendente. O simulador aparece quando ela sair.
      </Alert>
    );
  }

  const notaP2 = aplica(P2, p2) ?? 0;
  const notaP3 = d.p3 === 'composta' ? (aplica(P3_COMPOSTA, p3) ?? 0) : p3.P3;
  const sem = situacao({ P1: p1, P2: notaP2 });
  const com = situacao({ P1: p1, P2: notaP2, P3: notaP3 });
  const alvoP2 = p2Necessaria(p1);
  const indComTudo10 = componenteNecessario(P2, alvoP2, { T2: 10, GRU2: 10 }, 'IND2');
  const indRepetindo = componenteNecessario(
    P2,
    alvoP2,
    { T2: repetir.T2, GRU2: repetir.GRU2 },
    'IND2',
  );

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                O que você precisa
              </Typography>
              <Typography variant="h5" component="p" sx={{ mb: 1 }}>
                {alvoP2 <= 10 ? `${fmt(alvoP2)} na P2` : 'A P3 vai ser necessária'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Com P1 = {fmt(p1)}, a média (P1 + P2) / 2 chega a 6 com P2 ≥ {fmt(alvoP2)}.
                {alvoP2 <= 10 && (
                  <>
                    {' '}
                    Repetindo na T2 e no grupo o que fez na P1,{' '}
                    {indRepetindo <= 0
                      ? 'a nota individual nem pesa: você já fecha.'
                      : indRepetindo <= 10
                        ? `a individual da P2 precisa ser pelo menos ${fmt(indRepetindo)}.`
                        : 'só a individual não basta: capriche também na T2 e no grupo.'}{' '}
                    {indComTudo10 > 0 &&
                      indComTudo10 <= 10 &&
                      `Com 10 na T2 e no grupo, bastam ${fmt(indComTudo10)} na individual.`}
                  </>
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Nesta simulação
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1 }}>
                {sem.tipo === 'aprovado' ? (
                  <CheckCircleRounded color="success" />
                ) : (
                  <HourglassTopRounded color="warning" />
                )}
                <Typography variant="h5" component="p">
                  {sem.tipo === 'aprovado'
                    ? `Aprovado com ${fmt(sem.mf)}`
                    : com.tipo === 'aprovado'
                      ? `Aprovado na P3 com ${fmt(com.mf)}${com.arredondada ? ' (arredondada)' : ''}`
                      : `Média ${fmt(com.mf)}: ainda não`}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                P2 simulada: <strong>{fmt(notaP2)}</strong> · média (P1 + P2) / 2 ={' '}
                <strong>{fmt(sem.mf)}</strong>
                {sem.tipo !== 'aprovado' && (
                  <>
                    {' '}
                    · na P3 você precisaria de pelo menos{' '}
                    <strong>{fmt(p3Necessaria(p1, notaP2))}</strong>
                  </>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Sempre com frequência ≥ 75% (confira no SIGA).
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ justifyContent: 'space-between', mb: 2 }}
          >
            <Typography variant="h6" component="h2">
              Simule a P2
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" startIcon={<ReplayRounded />} onClick={() => setP2(repetir)}>
                Repetir a P1
              </Button>
              <Button size="small" onClick={() => setP2({ T2: 10, IND2: 10, GRU2: 10 })}>
                Tudo 10
              </Button>
            </Stack>
          </Stack>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controle
                rotulo="AWS (T2)"
                peso={0.3}
                valor={p2.T2}
                onChange={(v) => setP2((s) => ({ ...s, T2: v }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controle
                rotulo="Individual"
                peso={0.5}
                valor={p2.IND2}
                onChange={(v) => setP2((s) => ({ ...s, IND2: v }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Controle
                rotulo="Grupo"
                peso={0.2}
                valor={p2.GRU2}
                onChange={(v) => setP2((s) => ({ ...s, GRU2: v }))}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {sem.tipo !== 'aprovado' && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              E se for para a P3?
            </Typography>
            <Grid container spacing={3}>
              {d.p3 === 'composta' ? (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controle
                      rotulo="Exercícios do semestre"
                      peso={0.3}
                      valor={p3.EX3}
                      onChange={(v) => setP3((s) => ({ ...s, EX3: v }))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controle
                      rotulo="Projeto individual"
                      peso={0.7}
                      valor={p3.IND3}
                      onChange={(v) => setP3((s) => ({ ...s, IND3: v }))}
                    />
                  </Grid>
                </>
              ) : (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controle
                    rotulo="P3 (projeto individual)"
                    peso={1}
                    valor={p3.P3}
                    onChange={(v) => setP3((s) => ({ ...s, P3: v }))}
                  />
                </Grid>
              )}
            </Grid>
            <Typography variant="body2" color="text.secondary">
              P3 = {fmt(notaP3)} · média = (maior entre P1 e P2 + P3) / 2 ={' '}
              <strong>{fmt(com.mf)}</strong>. Quem faz a P3 e chega a 5,75 tem a nota arredondada
              para 6.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
